import { useEffect, useState } from "react";
import { Wallet, Check, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import { openWhatsApp } from "@/lib/whatsapp";

function formatRp(n) { return `Rp ${Number(n || 0).toLocaleString("id-ID")}`; }

const REMINDER_MESSAGE = (p) => {
  if (p.status === "fully_paid") return `Assalamu'alaikum ${p.jamaah_name},\n\nPembayaran Anda sebesar ${formatRp(p.total_fee)} telah kami terima dan lunas. Terima kasih atas kepercayaan Anda kepada KBIHU Muhammadiyah Surabaya.`;
  if (p.status === "submitted") return `Assalamu'alaikum ${p.jamaah_name},\n\nBukti pembayaran Anda sedang kami verifikasi. Kami akan menghubungi Anda kembali setelah proses verifikasi selesai.`;
  return `Assalamu'alaikum ${p.jamaah_name},\n\nMengingatkan kembali tagihan pembayaran program Anda di KBIHU Muhammadiyah Surabaya:\nTotal: ${formatRp(p.total_fee)}\nTerbayar: ${formatRp(p.amount_paid)}\n\nSilakan login ke dashboard Anda untuk melihat detail rekening pembayaran dan mengunggah bukti transfer.`;
};

export default function AdminPembayaran() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");

  const load = () => {
    const params = status !== "all" ? { status } : {};
    axios.get(`${API}/admin/pembayaran`, { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [status]);

  const verify = async (id, total) => {
    try {
      await axios.put(`${API}/admin/pembayaran/${id}/verify`, { amount_paid: total });
      toast.success("Pembayaran diverifikasi");
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const reject = async (id) => {
    try {
      await axios.put(`${API}/admin/pembayaran/${id}/reject`, {});
      toast.success("Pembayaran ditolak");
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-pembayaran-page" className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Pembayaran</h1>
        <p className="text-sm text-[#64748B]">Pantau dan verifikasi pembayaran jamaah.</p>
      </div>

      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="admin-pembayaran-status-filter" className="h-10 w-full rounded-xl sm:w-52"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua Status</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="submitted">Diajukan</SelectItem>
          <SelectItem value="verified">Terverifikasi</SelectItem>
          <SelectItem value="fully_paid">Lunas</SelectItem>
          <SelectItem value="rejected">Ditolak</SelectItem>
        </SelectContent>
      </Select>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Wallet} title="Belum ada data pembayaran" description="Data pembayaran jamaah akan tampil di sini." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-pembayaran-table">
            <TableHeader><TableRow><TableHead>Jamaah</TableHead><TableHead>Total</TableHead><TableHead>Terbayar</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`admin-pembayaran-row-${p.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{p.jamaah_name}</TableCell>
                  <TableCell className="text-[#64748B]">{formatRp(p.total_fee)}</TableCell>
                  <TableCell className="text-[#64748B]">{formatRp(p.amount_paid)}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {p.status === "submitted" && (
                        <>
                          <Button size="sm" data-testid={`admin-pembayaran-verify-${p.id}`} onClick={() => verify(p.id, p.total_fee)} className="gap-1 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Check className="h-3 w-3" /> Verifikasi</Button>
                          <Button size="sm" variant="outline" data-testid={`admin-pembayaran-reject-${p.id}`} onClick={() => reject(p.id)} className="gap-1 rounded-full border-red-200 text-red-600 hover:bg-red-50"><X className="h-3 w-3" /> Tolak</Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" data-testid={`admin-pembayaran-wa-${p.id}`} onClick={() => openWhatsApp(p.jamaah_phone, REMINDER_MESSAGE(p))} className="gap-1 rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"><MessageCircle className="h-3 w-3" /> Kirim WA</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
